export const CLAUDE_MODEL = "claude-sonnet-5";

// fmxFetch proxies requests through /api/fmx. Two accepted shapes:
//   1. { projectId, endpoint, ... }                         ← preferred for saved creds
//   2. { siteUrl, email, password, endpoint, ... }          ← only verify-before-save flows
// Plaintext credentials should only appear in shape (2), and only during
// the initial credential setup, to keep passwords off the wire thereafter.
export async function fmxFetch(opts) {
  const { projectId, siteUrl, email, password, endpoint, method, payload } = opts || {};
  const body = projectId
    ? { projectId, endpoint, method, payload }
    : { siteUrl, email, password, endpoint, method, payload };
  const res = await fetch('/api/fmx', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res;
}

// Pull a human-readable message out of an FMX error response body. FMX 400s
// carry field-level detail in validationErrors ([{ field, messages: [] }]) —
// flatten that first since `message` alone is generic ("Model invalid").
// Other failures use message / error / errors / modelState, so check those in
// order before falling back to the bare status. Shared by push, retry, undo.
export function fmxErrorMessage(body, status) {
  if (!body) return `HTTP ${status}`;
  if (Array.isArray(body.validationErrors) && body.validationErrors.length > 0) {
    const flat = body.validationErrors
      .map(ve => `${ve.field}: ${(ve.messages || []).join('; ')}`)
      .join(' | ');
    return body.message ? `${body.message} — ${flat}` : flat;
  }
  return body.message || body.error
    || (body.errors ? JSON.stringify(body.errors) : "")
    || (body.modelState ? JSON.stringify(body.modelState) : "")
    || `HTTP ${status}`;
}

// Run many FMX sub-requests in one round trip via the proxy's batch mode.
// `items` is [{ key, endpoint, method, payload, chain? }, ...] where `key` is an
// opaque client correlation token echoed back, and `chain` (optional) is a
// one-level follow-up { endpointTemplate, method, payload } whose "{id}" is
// substituted server-side with the parent record's id (used for Work Request
// assignments). Returns the per-item results array, aligned to `items` order:
//   [{ key, status, ok, body, headers, chain, attempts }, ...]
// The whole call rejects only on a transport/credential failure; individual
// sub-request failures come back as per-item results with ok:false.
export async function fmxBatchFetch({ projectId, items }) {
  const res = await fetch('/api/fmx', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, batch: items }),
  });
  if (!res.ok) {
    let msg = `Batch request failed: ${res.status}`;
    try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  return data.results || [];
}

// Split `items` into chunks and send them SEQUENTIALLY through fmxBatchFetch, so
// the server's worker-pool concurrency stays the global in-flight ceiling against
// the customer's FMX site. `onChunk(chunkResults, doneCount, total)` fires after
// each chunk (drives progress UIs). `signal` (AbortController) is checked between
// chunks — cancel takes effect after the current chunk completes, so its rows are
// still reported (and may have already landed server-side). Returns all results.
export async function fmxBatchAll({ projectId, items, chunkSize = 25, signal, onChunk }) {
  const all = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    if (signal?.aborted) break;
    const chunk = items.slice(i, i + chunkSize);
    const results = await fmxBatchFetch({ projectId, items: chunk });
    for (const r of results) all.push(r);
    if (onChunk) onChunk(results, all.length, items.length);
  }
  return all;
}

// Download an FMX attachment by its `downloadUrl` (returned from
// /v1/attachments/{id}) via the binary mode of /api/fmx. The server injects
// the project's saved Basic-auth credentials, fetches the bytes, and returns
// them base64-encoded so the browser can hand the data to Claude vision
// without the password or raw URL ever touching the browser.
export async function fmxAttachmentDownload({ projectId, downloadUrl }) {
  const res = await fetch('/api/fmx', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, mode: 'binary', attachmentDownloadUrl: downloadUrl }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // Surface the exact server branch in the console so downstream callers
    // (which often swallow the message in per-attachment try/catch) don't
    // hide it. The downloadUrl is logged too so hostname issues are easy to
    // spot in the network tab.
    console.error('[fmxAttachmentDownload]', res.status, data?.error || '(no error body)', { downloadUrl });
    throw new Error(data?.error || `Attachment download failed: ${res.status}`);
  }
  return data; // { base64, contentType, filename, byteCount }
}

// Save/encrypt FMX credentials server-side. Plaintext password crosses the
// browser/server boundary exactly once (here); thereafter the client refers
// to them only by projectId.
export async function saveFmxCredentialsRequest({ projectId, siteUrl, email, password, verified }) {
  const res = await fetch('/api/fmx-credentials', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, siteUrl, email, password, verified }),
  });
  if (!res.ok) {
    let msg = 'Failed to save credentials';
    try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  return data.project;
}

export async function clearFmxCredentialsRequest(projectId) {
  const res = await fetch('/api/fmx-credentials', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId }),
  });
  if (!res.ok) throw new Error('Failed to clear credentials');
  const data = await res.json();
  return data.project;
}

export async function claudeFetch({ messages, max_tokens, system }) {
  // claude-sonnet-5 runs adaptive thinking when `thinking` is omitted. Our
  // call sites use small max_tokens budgets sized for text-only output, so
  // disable thinking explicitly to keep the old response shape and spend.
  const body = { model: CLAUDE_MODEL, max_tokens, messages, thinking: { type: 'disabled' } };
  if (system) body.system = system;
  const res = await fetch('/api/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) {
    const err = new Error(
      res.status === 529
        ? "Claude is temporarily overloaded — please try again in a moment."
        : res.status === 429
          ? "Rate limit reached — please wait a moment and try again."
          : res.status === 404
            ? `The configured Claude model ("${CLAUDE_MODEL}") is unavailable or retired — update CLAUDE_MODEL in src/apiClient.js.`
            : data.error?.message || "AI request failed."
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export function parseClaudeText(data) {
  // Find the text block rather than assuming content[0] — models with
  // thinking enabled prepend a thinking block, which would blank the result.
  const text = data.content?.find(b => b.type === 'text')?.text || "";
  return text.replace(/```json|```javascript|```js|```/g, "").trim();
}
